import React, { useState, useEffect } from 'react';
import { Gem, Sparkles, Truck, ShieldCheck, RefreshCcw, Award } from 'lucide-react';
import { ABOUT_CONTENT as FALLBACK_ABOUT } from '../mock';
import mathsStoryImage from '../assets/images/about/maths.jpg';
import wornStoryImage from '../assets/images/about/worn.jpg';

/* ── "At Your Service" items ── */
const SERVICE_ITEMS = [
  { label: 'IGI Certified Diamonds', Icon: Gem },
  { label: '24K Gold Vermeil',       Icon: Sparkles },
  { label: 'BIS Hallmarked Silver',  Icon: Award },
  { label: 'Secure Shipping',        Icon: Truck },
  { label: 'Lifetime Buyback',       Icon: ShieldCheck },
  { label: '7-Day Easy Returns',     Icon: RefreshCcw },
];

const StoryRow = ({ title, body, image, reverse = false }) => (
  <div
    className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center${
      reverse ? ' lg:[&>*:first-child]:order-2' : ''
    }`}
  >
    <div className="aspect-[4/5] overflow-hidden bg-[#f7f4ee]">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
    <div>
      <h2 className="font-serif text-3xl lg:text-5xl leading-tight text-[#1a1a1a]">
        {title}
      </h2>
      <p className="mt-6 text-[15px] text-[#5a5346] leading-relaxed max-w-lg whitespace-pre-line">
        {body}
      </p>
    </div>
  </div>
);

const ServiceCard = ({ label, Icon }) => (
  <div className="flex flex-col items-center justify-center text-center gap-4 bg-[#FFFFFF] border border-[#e5ddce] px-6 py-10 w-full h-full">
    <div className="w-20 h-20 rounded-full bg-[#f7f4ee] flex items-center justify-center flex-shrink-0">
      <Icon size={34} strokeWidth={1.3} className="text-[#999959]" />
    </div>
    <span className="text-[15px] leading-snug tracking-wide text-[#1a1a1a]">
      {label}
    </span>
  </div>
);

const AtYourService = () => (
  <section className="py-14 lg:py-20 bg-[#FFFFFF] border-t border-[#e5ddce]">
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
      <div className="text-center mb-10 lg:mb-14">
        <h2 className="font-serif text-3xl lg:text-4xl text-[#1a1a1a]">
          At Your Service
        </h2>
      </div>

      <div className="flex lg:hidden overflow-x-auto snap-x snap-mandatory no-scrollbar gap-4 -mx-6 px-6">
        {SERVICE_ITEMS.map(({ label, Icon }) => (
          <div
            key={label}
            className="flex-shrink-0 snap-center w-[72vw] max-w-[280px] aspect-[4/5]"
          >
            <ServiceCard label={label} Icon={Icon} />
          </div>
        ))}
      </div>

      <div className="hidden lg:grid lg:grid-cols-6 gap-4 xl:gap-6">
        {SERVICE_ITEMS.map(({ label, Icon }) => (
          <div key={label} className="aspect-[4/5]">
            <ServiceCard label={label} Icon={Icon} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const About = () => {
  const pageData = null;

  const heroImage = pageData?.hero?.image || FALLBACK_ABOUT.hero.image;
  const heroTitle = pageData?.hero?.title || 'Real Diamonds. Without the Weight.';
  const heroBody = pageData?.hero?.body || "Fine jewelry has always asked you to choose — between what's real and what's affordable, between wearing it and protecting it, between today and someday. We think that's the wrong trade-off. So we built something else.";

  const mathsTitle = pageData?.maths?.title || "Because the Maths Stopped Making Sense";
  const mathsBody = pageData?.maths?.body || `Lab-grown diamonds changed the industry for good reason — the same brilliance, the same certification, a fraction of the cost of a mined stone. For the first time, a real diamond felt within reach.\n\nThen gold prices climbed, and for a lot of buyers, that same feeling of accessibility quietly disappeared again. The diamond got more affordable. The setting didn't. Fine jewelry ended up right back where it started — out of reach for most people, just for a different reason.\n\nHouse of Seya exists to close that gap properly. Real, IGI-certified lab-grown diamonds, set in richly plated 24K gold vermeil instead of solid gold — so the accessibility lab-grown diamonds promised in the first place actually reaches you, not just the diamond counter.`;
  const mathsImage = pageData?.maths?.image || mathsStoryImage;

  const wornTitle = pageData?.worn?.title || "Worn, Not Stored";
  const wornBody = pageData?.worn?.body || `We don't think fine jewelry should spend its life in a locker, waiting for an occasion big enough to justify wearing it. A diamond that only comes out twice a year isn't doing its job.\n\nEvery House of Seya piece is built to be worn constantly — to your desk, your dinner, your ordinary Tuesday. Not because it's less valuable, but because we believe the whole point of owning something beautiful is actually living in it.`;
  const wornImage = pageData?.worn?.image || wornStoryImage;

  const founderTitle = pageData?.founder?.title || "Built in Bangalore, by Two People Who Saw the Same Gap";
  const founderImage = pageData?.founder?.image || FALLBACK_ABOUT.founder.image;

  const expTitle = pageData?.experience?.title || "The House of Seya Experience";
  const expBody = pageData?.experience?.body || `We'd rather earn your trust with documentation than ask for it on faith. Every diamond ships with its IGI certificate. Every silver base carries a BIS hallmark. Every claim we make about our materials is one you can independently verify, not just take our word for.\n\nFrom your first browse to years down the line, we're here — for customisation, for care questions, and through our Lifetime Buyback Program, for whenever your taste or your life moves on from a piece you once loved.`;
  const expImage = pageData?.experience?.image || FALLBACK_ABOUT.experience.image;

  return (
    <main className="bg-[#FFFFFF]">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[320px] lg:h-[65vh] hero-vh-50-65 overflow-hidden">
        <img
          src={heroImage}
          alt={heroTitle}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
          <div className="text-center text-white px-6 max-w-3xl">
            <h1 className="font-serif text-4xl lg:text-6xl leading-tight">
              {heroTitle}
            </h1>
            <p className="mt-5 text-[15px] lg:text-[17px] text-white/85 leading-relaxed max-w-2xl mx-auto">
              {heroBody}
            </p>
          </div>
        </div>
      </div>

      {/* Section 2: Because the Maths Stopped Making Sense */}
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <StoryRow
          title={mathsTitle}
          body={mathsBody}
          image={mathsImage}
        />
      </div>

      {/* Section 3: Worn, Not Stored */}
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32">
        <StoryRow
          title={wornTitle}
          body={wornBody}
          image={wornImage}
          reverse
        />
      </div>

      {/* Section 4: Founders */}
      <div className="bg-[#f7f4ee] py-24 lg:py-32">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[440px_1fr] gap-10 lg:gap-16 items-center">
          <div className="aspect-[4/5] overflow-hidden bg-[#FFFFFF] max-w-[400px] mx-auto lg:mx-0">
            <img
              src={founderImage}
              alt="Founders of House of Seya"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h2 className="font-serif text-3xl lg:text-5xl leading-tight text-[#1a1a1a]">
              {founderTitle}
            </h2>
            <p className="mt-6 text-[15px] text-[#5a5346] leading-relaxed max-w-lg">
              House of Seya was founded by Gaurav Mutha and Mohit Gulecha, and built from Bangalore.
            </p>
            <p className="mt-4 text-[15px] text-[#5a5346] leading-relaxed max-w-lg">
              Gaurav spent 15 years inside the diamond jewelry industry, close enough to watch lab-grown diamonds shift from a niche alternative to a genuine, credible choice.
            </p>
            <p className="mt-4 text-[15px] text-[#5a5346] leading-relaxed max-w-lg">
              Mohit spent over 15 years building and running retail businesses across categories, learning first-hand what it actually takes to make a product both trustworthy and genuinely accessible at scale.
            </p>
            <p className="mt-4 text-[15px] text-[#5a5346] leading-relaxed max-w-lg">
              Between the two of them, the gap was obvious: the diamond had already been solved. The setting hadn't. House of Seya is what happened when they decided to fix that too.
            </p>
          </div>
        </div>
      </div>

      {/* Section 5: The House of Seya Experience */}
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <StoryRow
          title={expTitle}
          body={expBody}
          image={expImage}
          reverse
        />
      </div>

      {/* Section 6: At Your Service carousel */}
      <AtYourService />
    </main>
  );
};

export default About;